import { EncryptionMode } from '@gofranz/formshive-common';
import { Alert, Anchor, Box, Button, Checkbox, Group, Select, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertCircle, IconDeviceFloppy, IconLock, IconX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Values this component manages locally
export interface FormFieldsValues {
  title: string;
  filter_spam: boolean;
  check_challenge: boolean;
  redirect_url: string | undefined;
  auto_response_enabled: boolean;
  encryption_mode?: EncryptionMode;
}

export interface FormFieldsProps {
  isEditing: boolean;
  isBusy: boolean;
  hasError: {
    title: string;
    message: string;
  } | null;
  fieldErrors?: Record<string, string>;
  submitCb: () => Promise<void>;
  // Initial values from parent (only used on mount and reset)
  initialValues: FormFieldsValues;
  // Callback to report changes to parent
  onValuesChange: (values: Partial<FormFieldsValues>) => void;
  formSubmitUrl: string;
  hasUnsavedChanges: boolean;
  onCancel: () => void;
  // Reset trigger - increment to reset local state
  resetKey?: number;
  /** Whether the current user has a Nostr public key (required for nostr encryption) */
  hasNostrPubkey?: boolean;
}

export function FormFields(props: FormFieldsProps) {
  const { t } = useTranslation();

  // LOCAL STATE - for instant typing feedback (like AutoResponse pattern)
  const [localTitle, setLocalTitle] = useState(props.initialValues.title);
  const [localRedirectUrl, setLocalRedirectUrl] = useState(props.initialValues.redirect_url || '');
  const [localFilterSpam, setLocalFilterSpam] = useState(props.initialValues.filter_spam);
  const [localCheckChallenge, setLocalCheckChallenge] = useState(props.initialValues.check_challenge);
  const [localEncryptionMode, setLocalEncryptionMode] = useState<EncryptionMode | undefined>(
    props.initialValues.encryption_mode
  );

  // DEBOUNCED VALUES - only sync to parent after 300ms of no typing
  const [debouncedTitle] = useDebouncedValue(localTitle, 300);
  const [debouncedRedirectUrl] = useDebouncedValue(localRedirectUrl, 300);

  // Reset local state when resetKey changes (cancel operation)
  useEffect(() => {
    setLocalTitle(props.initialValues.title);
    setLocalRedirectUrl(props.initialValues.redirect_url || '');
    setLocalFilterSpam(props.initialValues.filter_spam);
    setLocalCheckChallenge(props.initialValues.check_challenge);
    setLocalEncryptionMode(props.initialValues.encryption_mode);
  }, [props.resetKey]);

  // Sync debounced text values to parent - ALWAYS update ref, let parent compute dirty
  useEffect(() => {
    props.onValuesChange({ title: debouncedTitle });
  }, [debouncedTitle]);

  useEffect(() => {
    // Convert empty string to undefined for consistency
    const value = debouncedRedirectUrl || undefined;
    props.onValuesChange({ redirect_url: value });
  }, [debouncedRedirectUrl]);

  // Sync checkbox changes immediately (they don't need debounce)
  const handleFilterSpamChange = (checked: boolean) => {
    setLocalFilterSpam(checked);
    props.onValuesChange({ filter_spam: checked });
  };

  const handleCheckChallengeChange = (checked: boolean) => {
    setLocalCheckChallenge(checked);
    props.onValuesChange({ check_challenge: checked });
  };

  const handleEncryptionModeChange = (value: EncryptionMode) => {
    setLocalEncryptionMode(value);
    props.onValuesChange({ encryption_mode: value });
  };

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          props.submitCb();
        }}
      >
        <TextInput
          label={t('forms.title')}
          placeholder={t('forms.titlePlaceholder')}
          value={localTitle}
          onChange={(event) => setLocalTitle(event.currentTarget.value)}
          error={props.fieldErrors?.title && t('forms.titleRequired')}
          mb="xs"
          required
          description={t('forms.titleDescription')}
        />
        <TextInput
          label={t('forms.redirectUrl')}
          placeholder={t('forms.redirectPlaceholder')}
          value={localRedirectUrl}
          onChange={(event) => setLocalRedirectUrl(event.currentTarget.value)}
          error={props.fieldErrors?.redirect_url && t('forms.invalidUrl')}
          mb="xs"
          description={t('forms.redirectDescription')}
        />
        {!localRedirectUrl && (
          <Alert variant="light" color="primary" icon={<IconAlertCircle size={20} />} mb="md">
            {t('forms.redirectWarning')}
          </Alert>
        )}
        <Checkbox
          label={t('forms.filterSpam')}
          checked={localFilterSpam}
          onChange={(event) => handleFilterSpamChange(event.currentTarget.checked)}
          mb="xs"
          description={t('forms.filterSpamDescription')}
        />
        {localFilterSpam && (
          <Alert variant="light" color="primary" icon={<IconAlertCircle size={20} />} mb="md">
            {t('forms.filterSpamWarning')}
          </Alert>
        )}
        <Checkbox
          label={t('forms.checkCaptcha')}
          checked={localCheckChallenge}
          onChange={(event) => handleCheckChallengeChange(event.currentTarget.checked)}
          mb="xs"
          description={t('forms.captchaDescription')}
        />
        {localCheckChallenge && (
          <Alert variant="light" color="primary" icon={<IconAlertCircle size={20} />} mb="md">
            {t('forms.captchaWarning')}{' '}
            <Anchor href="#/account/docs?section=captcha-setup">{t('forms.setupInstructions')}</Anchor>
          </Alert>
        )}

        {/* Encryption mode - only show when creating a form (immutable after creation) */}
        {!props.isEditing && (
          <>
            <Select
              label={t('forms.encryptionMode')}
              description={t('forms.encryptionModeDescription')}
              leftSection={<IconLock size={16} />}
              value={localEncryptionMode || 'none'}
              onChange={(value) => handleEncryptionModeChange(value as EncryptionMode)}
              data={[
                { value: 'none', label: t('forms.encryptionNone') },
                { value: 'server', label: t('forms.encryptionServer') },
                ...(props.hasNostrPubkey
                  ? [{ value: 'nostr', label: t('forms.encryptionNostr') }]
                  : []),
              ]}
              error={props.fieldErrors?.encryption_mode}
              mb="xs"
            />
            {localEncryptionMode === 'server' && (
              <Alert variant="light" color="blue" icon={<IconLock size={20} />} mb="md">
                {t('forms.encryptionServerWarning')}
              </Alert>
            )}
            {localEncryptionMode === 'nostr' && (
              <Alert variant="light" color="green" icon={<IconLock size={20} />} mb="md">
                {t('forms.encryptionNostrWarning')}
              </Alert>
            )}
            {!props.hasNostrPubkey && (
              <Alert variant="light" color="gray" icon={<IconAlertCircle size={20} />} mb="md">
                {t('forms.encryptionNostrRequiresLogin')}
              </Alert>
            )}
          </>
        )}

        <Box mb="md">
          <Group>
            <Button
              type="submit"
              loading={props.isBusy}
              disabled={props.isBusy}
              leftSection={<IconDeviceFloppy />}
              color={props.hasUnsavedChanges ? 'yellow' : ''}
            >
              {props.isEditing
                ? props.hasUnsavedChanges
                  ? t('glob_buttons.updateUnsaved')
                  : t('glob_buttons.update')
                : t('glob_buttons.create')}
            </Button>
            {props.hasUnsavedChanges && (
              <Button
                onClick={props.onCancel}
                loading={props.isBusy}
                disabled={props.isBusy}
                leftSection={<IconX />}
                variant="light"
                color="gray"
              >
                {t('glob_buttons.cancel')}
              </Button>
            )}
          </Group>

          {props.hasError && (
            <Alert mt="md" icon={<IconAlertCircle size={16} />} title={props.hasError.title} color="red">
              {props.hasError.message}
            </Alert>
          )}
        </Box>
      </form>
    </>
  );
}
